import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '@/lib/axios';
import { API_BASE_URL } from '@/config';
import Landing from '@/components/LOS/Caprion/Landing';
import Consent from '@/components/LOS/Caprion/Consent';
import MobileOTPLogin from '@/apps/los/components/MobileOTPLogin/MobileOTPLogin';
import BrandedJourneyHeader from '@/components/BrandedJourneyHeader';
import OpenInBrowserBanner from '@/components/OpenInBrowserBanner';

/**
 * Index - Pre-login journey for /index?bap=PhonePe&source=phonepe-term-loan
 *
 * This component manages the first 3 pages locally (no backend journey state):
 * 1. Landing Page - Features and "Apply Now" CTA
 * 2. Consent Page - Data sharing consent acceptance
 * 3. Login Page - OTP-based authentication
 *
 * After login, the user is redirected to /loan-application where the
 * backend-managed journey takes over (lender selected on page 3).
 *
 * URL stays /index?bap=PhonePe&source=phonepe-term-loan throughout the pre-login flow.
 *
 * IMPORTANT: For PhonePe journeys, source parameter is mandatory and validated before showing landing page.
 */
const Index = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Local state for pre-login pages (1=Landing, 2=Consent, 3=Login)
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);  // Start with loading true for validation
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Validate source on mount (before showing landing page)
  useEffect(() => {
    const validateSource = async () => {
      try {
        const source = searchParams.get('source');
        const bap = searchParams.get('bap') || 'PhonePe';
        
        console.log('[Index] Validating source:', { source, bap });
        
        const response = await axios.post(`${API_BASE_URL}/api/v3/validate-source/`, {
          source: source,
          bap: bap
        }, {
          withCredentials: true
        });
        
        if (response.data.valid) {
          console.log('[Index] Source validation successful');
          setIsValidating(false);
          setIsLoading(false);
        } else {
          // This shouldn't happen as API returns 400 for invalid
          console.error('[Index] Source validation returned invalid');
          navigate('/journey-error?type=invalid_source');
        }
      } catch (error: any) {
        console.error('[Index] Source validation failed:', error);
        
        if (error.response?.status === 400) {
          // Invalid or missing source - redirect to error page
          const errorType = error.response.data.error || 'invalid_source';
          console.log('[Index] Redirecting to error page, type:', errorType);
          // Use navigate with query params instead of full URL
          navigate(`/journey-error?type=${errorType}`);
        } else {
          // Other errors - show generic error
          console.error('[Index] Unexpected error:', error);
          setError('Failed to validate journey link. Please try again.');
          setIsValidating(false);
          setIsLoading(false);
        }
      }
    };
    
    validateSource();
  }, [searchParams, navigate]);
  
  /**
   * Silently pre-warm camera + location permissions on the "Get Started" tap.
   * Requests are sequential (location first, then camera) so the browser
   * doesn't suppress the second popup while the first is still showing.
   * Never blocks the journey — resolves regardless of grant/deny.
   */
  const _prewarmPermissions = async (): Promise<void> => {
    // 1. Location first
    await new Promise<void>((resolve) => {
      if (!navigator.geolocation) { resolve(); return; }
      navigator.geolocation.getCurrentPosition(
        () => { console.log('[Index] Location permission pre-warmed'); resolve(); },
        (err) => { console.log('[Index] Location pre-warm skipped:', err.message); resolve(); },
        { timeout: 10000, maximumAge: 60000 }
      );
    });

    // 2. Camera second — after location popup is dismissed
    if (navigator.mediaDevices?.getUserMedia) {
      await navigator.mediaDevices
        .getUserMedia({ video: true, audio: false })
        .then((stream) => {
          stream.getTracks().forEach((t) => t.stop());
          console.log('[Index] Camera permission pre-warmed');
        })
        .catch((err) => {
          console.log('[Index] Camera pre-warm skipped:', err.name);
        });
    }
  };

  // Handle "Apply Now" click on Landing page
  const handleApplyNow = async () => {
    // Pre-warm permissions sequentially — location popup first, then camera.
    // Both are awaited so the journey only advances after both popups are
    // dismissed (or timed out). The user gesture is still valid across awaits
    // for getUserMedia because it was initiated in the same task.
    // Fire without blocking the loading state — run concurrently with the API call.
    const permissionsPromise = _prewarmPermissions();

    setIsLoading(true);
    try {
      // Call set_bap_identifier_cookie API with PhonePe as BAP
      await axios.post(`${API_BASE_URL}/api/set_bap_identifier_cookie/`, {
        enc_id: 'WHITESPACE',
        bap: 'PhonePe'
      }, {
        withCredentials: true
      });

      // Wait for permissions to finish (or timeout) before advancing,
      // so the camera popup doesn't appear on top of the consent page.
      await permissionsPromise;

      // Move to consent page
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to set BAP identifier:', error);
      setError('Failed to initialize session. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Handle navigation between pre-login pages
  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Handle successful login - redirect to journey
  const handleLoginSuccess = () => {
    // After login, redirect to the main journey which starts at Personal Information
    console.log('[Index] Login successful, redirecting to journey...');
    
    // Use window.location.href for a hard redirect to ensure it works
    // Journey is already assigned via source_slug in session, no need to pass it in URL
    window.location.href = '/loan-application';
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-intellend-green to-intellend-green/80 flex items-center justify-center">
        <div className="text-center">
          {/* Beautiful spinner with multiple rings */}
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-white/20"></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-white animate-spin"></div>
            <div className="absolute inset-2 rounded-full border-4 border-transparent border-t-intellend-gold animate-spin" style={{ animationDuration: '1.5s' }}></div>
          </div>
          <p className="text-white text-sm font-medium">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-intellend-green text-white rounded-lg hover:bg-intellend-green/90 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }
  
  // Render current step - Mobile-first design
  return (
    <>
      {/* Open-in-browser banner — renders only when inside a webview (WhatsApp, Instagram, etc.)
          Self-hides in real browsers, so safe to mount unconditionally on every step. */}
      <OpenInBrowserBanner reason="You opened this from an app. Chrome or Safari keeps you signed in and lets camera, location and document upload work reliably through the application." />

      {/* Step 1: Landing Page */}
      {currentStep === 1 && (
        <Landing
          onNext={handleApplyNow}
          isLoading={isLoading}
        />
      )}
      
      {/* Step 2: Consent Page */}
      {currentStep === 2 && (
        <Consent
          onNext={handleNext}
          onBack={handleBack}
        />
      )}
      
      {/* Step 3: Login Page */}
      {currentStep === 3 && (
        <div className="min-h-screen bg-intellend-green flex flex-col">
          {/* Header with Branded Header - Full width, no gaps */}
          <div className="bg-intellend-green">
            {/* Branded Journey Header */}
            <BrandedJourneyHeader />
          </div>

          {/* Login Form */}
          <div className="flex-1 bg-gray-50 px-4 py-6">
            <div className="w-full max-w-md mx-auto bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Login to Continue</h2>
              <p className="text-sm md:text-base text-gray-600 mb-6">Enter your mobile number to proceed with your loan application</p>
              
              <MobileOTPLogin
                onNext={handleLoginSuccess}
                onSuccess={handleLoginSuccess}
                onError={(errorMsg) => {
                  // Error already handled by MobileOTPLogin component
                  // This callback is just for logging/tracking
                  console.error('[Index] OTP verification error:', errorMsg);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Index;
